import { Tooltip as ChakraTooltip, Portal } from "@chakra-ui/react"
import * as React from "react"

export interface TooltipProps extends ChakraTooltip.RootProps {
  showArrow?: boolean
  portalled?: boolean
  portalRef?: React.RefObject<HTMLElement>
  content: React.ReactNode
  contentProps?: ChakraTooltip.ContentProps
  disabled?: boolean
}

/**
 * @useFor hover/focus tooltips revealing a label or short
 *   explanation on icons, truncated text, or compact controls
 *   where the meaning isn't visible at rest.
 *
 *   Examples in-tree: icon-only action buttons (Delete, Duplicate),
 *   truncated cell values in `<DataTable>`, status-badge hover
 *   detail.
 *
 *   For multi-line explainer content use a `<Popover>` (clickable,
 *   stays open). For form-field help text use Chakra's
 *   `<Field.HelperText>` — that's always visible and doesn't
 *   require a hover gesture.
 *
 *   `disabled` short-circuits the tooltip (renders children
 *   directly) so callers can conditionally show tooltips without
 *   branching the JSX.
 */
export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  function Tooltip(props, ref) {
    const {
      showArrow,
      children,
      disabled,
      portalled = true,
      content,
      contentProps,
      portalRef,
      ...rest
    } = props

    if (disabled) return children

    return (
      <ChakraTooltip.Root {...rest}>
        <ChakraTooltip.Trigger asChild>{children}</ChakraTooltip.Trigger>
        <Portal disabled={!portalled} container={portalRef}>
          <ChakraTooltip.Positioner>
            <ChakraTooltip.Content ref={ref} {...contentProps}>
              {showArrow && (
                <ChakraTooltip.Arrow>
                  <ChakraTooltip.ArrowTip />
                </ChakraTooltip.Arrow>
              )}
              {content}
            </ChakraTooltip.Content>
          </ChakraTooltip.Positioner>
        </Portal>
      </ChakraTooltip.Root>
    )
  },
)
